import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SellerAuthProvider, useSellerAuth } from './contexts/SellerAuthContext';
import { BuyerAuthProvider } from './contexts/BuyerAuthContext';
import BuyerLayout from './components/BuyerLayout';

// Public seller pages stay eager: /seller/register is in prerender.routes and
// both are reachable signed-out, where a chunk round-trip is the wrong trade.
import SellerRegister from './pages/seller/SellerRegister';
import SellerLogin from './pages/seller/SellerLogin';

// Buyer pages
import Homepage from './pages/buyer/Homepage';
import BrowseVehicles from './pages/buyer/BrowseVehicles';
import VehicleDetail from './pages/buyer/VehicleDetail';
import BuyerLogin from './pages/buyer/BuyerLogin';
import BuyerRegister from './pages/buyer/BuyerRegister';
import AboutUs from './pages/buyer/AboutUs';
import CityLanding from './pages/buyer/CityLanding';
import SellTaxi from './pages/buyer/SellTaxi';
import FAQ from './pages/buyer/FAQ';
import TaxiValuation from './pages/buyer/TaxiValuation';
import TPermitHub from './pages/buyer/TPermitHub';
import TPermitState from './pages/buyer/tpermit/TPermitState';
import Blog from './pages/buyer/Blog';
import NotFound from './pages/buyer/NotFound';
import InnovaCrystaHub from './pages/buyer/hubs/InnovaCrystaHub';
import FortunerHub from './pages/buyer/hubs/FortunerHub';
import DzireHub from './pages/buyer/hubs/DzireHub';
import ErtigaHub from './pages/buyer/hubs/ErtigaHub';
// NewTaxiIndex / NewTaxiModel are unmounted while the section is reworked —
// see the /new-taxi redirects below. The pages and their data files stay in
// the repo so work can continue; only the public exposure is withdrawn.

import './index.css';

// Admin and seller portals are lazy — neither is prerendered and no buyer ever
// loads one, so keeping them out of the entry chunk is free. The public seller
// pages above are the exception and stay eager.
// BlogPost is the only importer of data/blogPosts.js — 735 KB of article
// bodies. Lazy so that file lands in a /blog/<slug> chunk instead of the entry
// bundle every visitor downloads. The route IS prerendered, so the chunk has to
// resolve before the snapshot: prerender.js waits for networkidle0 plus a
// settle delay, which covers it, and blogIndex.test.js aside, the proof is that
// prerendered posts still contain their article text.
const BlogPost = lazy(() => import('./pages/buyer/BlogPost'));

const Login = lazy(() => import('./pages/Login'));
const AdminRoutes = lazy(() => import('./pages/admin/AdminRoutes'));
const SellerRoutes = lazy(() => import('./pages/seller/SellerRoutes'));

// Suspense boundary for the lazy portal chunks. Wrapped per-element rather than
// once around <Routes>, so an eagerly-imported buyer page can never be replaced
// by a loading state — the prerenderer snapshots those, and a fallback captured
// instead of the page would ship an empty body to crawlers.
// The fallback holds a full screen: /blog/<slug> is prerendered, but render()
// discards the snapshot and the lazy BlogPost chunk suspends first, so a short
// fallback pulled the footer up into view and the article pushed it back down —
// a 0.735 layout shift on every post (Lighthouse, 2026-09-26).
const Chunk = ({ children }) => (
  <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', minHeight: '100vh' }}>Loading...</div>}>
    {children}
  </Suspense>
);

const AdminProtectedRoute = ({ children }) => {
  const { admin, loading } = useAuth();
  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
};

const SellerProtectedRoute = ({ children }) => {
  const { seller, loading } = useSellerAuth();
  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  if (!seller) return <Navigate to="/seller/login" replace />;
  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Admin Routes */}
      <Route path="/admin/login" element={<Chunk><Login /></Chunk>} />
      <Route path="/admin/*" element={
        <AdminProtectedRoute>
          <Chunk><AdminRoutes /></Chunk>
        </AdminProtectedRoute>
      } />

      {/* Seller Routes */}
      <Route path="/seller/register" element={<SellerRegister />} />
      <Route path="/seller/login" element={<SellerLogin />} />
      <Route path="/seller/*" element={
        <SellerProtectedRoute>
          <Chunk><SellerRoutes /></Chunk>
        </SellerProtectedRoute>
      } />

      {/* Buyer Routes - Public */}
      <Route path="/buyer/login" element={<BuyerLayout><BuyerLogin /></BuyerLayout>} />
      <Route path="/buyer/register" element={<BuyerLayout><BuyerRegister /></BuyerLayout>} />
      <Route path="/vehicles/:slug" element={<BuyerLayout><VehicleDetail /></BuyerLayout>} />
      <Route path="/vehicles" element={<BuyerLayout><BrowseVehicles /></BuyerLayout>} />
      <Route path="/about" element={<BuyerLayout><AboutUs /></BuyerLayout>} />
      <Route path="/sell-taxi" element={<BuyerLayout><SellTaxi /></BuyerLayout>} />
      <Route path="/taxi-valuation" element={<BuyerLayout><TaxiValuation /></BuyerLayout>} />
      <Route path="/t-permit" element={<BuyerLayout><TPermitHub /></BuyerLayout>} />
      <Route path="/t-permit/by-state/:state" element={<BuyerLayout><TPermitState /></BuyerLayout>} />
      <Route path="/faq" element={<BuyerLayout><FAQ /></BuyerLayout>} />
      {['delhi','gurugram','noida','faridabad','ghaziabad','mumbai','bangalore','hyderabad','chennai','pune','kolkata','ahmedabad','jaipur','lucknow','surat','dehradun'].map(city => (
        <Route key={city} path={`/buy-taxi-${city}`} element={<BuyerLayout><CityLanding /></BuyerLayout>} />
      ))}
      <Route path="/blog/:slug" element={<BuyerLayout><Chunk><BlogPost /></Chunk></BuyerLayout>} />
      <Route path="/blog" element={<BuyerLayout><Blog /></BuyerLayout>} />

      {/* Vehicle Hub Pages — Phase 2 SEO (model-specific BOFU content) */}
      <Route path="/innova-crysta-commercial-taxi-delhi" element={<BuyerLayout><InnovaCrystaHub /></BuyerLayout>} />
      <Route path="/fortuner-commercial-taxi-delhi" element={<BuyerLayout><FortunerHub /></BuyerLayout>} />
      <Route path="/dzire-commercial-taxi-delhi" element={<BuyerLayout><DzireHub /></BuyerLayout>} />
      <Route path="/ertiga-commercial-taxi-delhi" element={<BuyerLayout><ErtigaHub /></BuyerLayout>} />

      {/* New-taxi research pages — TOFU/MOFU for buyers shopping a new commercial vehicle */}
      {/* New-taxi section is hidden while it is reworked. Redirect rather than
          404: the URLs were briefly live, in the sitemap and submitted to
          search engines, so anyone holding one should land somewhere useful
          and crawlers should see a temporary move, not a dead page.
          These Routes only cover in-app navigation. A crawler hitting the URL
          directly never runs React — it gets Vercel's catch-all rewrite, which
          served the prerendered homepage (homepage title, H1 and canonical) at
          200 for a week. The real redirect is the 301 pair in vercel.json;
          keep both, and do not drop those on the assumption these cover it. */}
      <Route path="/new-taxi/:slug" element={<Navigate to="/vehicles" replace />} />
      <Route path="/new-taxi" element={<Navigate to="/vehicles" replace />} />

      <Route path="/" element={<BuyerLayout><Homepage /></BuyerLayout>} />

      {/* Catch-all 404 — must stay last */}
      <Route path="*" element={<BuyerLayout><NotFound /></BuyerLayout>} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <SellerAuthProvider>
        <BuyerAuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </BuyerAuthProvider>
      </SellerAuthProvider>
    </AuthProvider>
  );
}

export default App;
